import { useCallback } from 'react';
import { useWLUser } from '../providers/WLUserContextProvider';
import type { CustomerUser } from '../types';

// Tabs
export const WL_BLOTTER_TAB = {
  ActiveOrders: 'ActiveOrders',
  Balances: 'Balances',
  Credits: 'Credits',
  OrderHistory: 'OrderHistory',
  Trades: 'Trades',
  Transfers: 'Transfers',
  FundingEvents: 'FundingEvents',
  Positions: 'Positions',
  Margin: 'Margin',
} as const;
export type WL_BLOTTER_TAB = (typeof WL_BLOTTER_TAB)[keyof typeof WL_BLOTTER_TAB];

// Permissions
export const WL_PERMISSIONS = {
  READ_MARKETDATA: 'read::marketdata' as const,
  READ_ORDERS: 'read::orders' as const,
  WRITE_ENTITY: 'write::entity' as const,
  WRITE_ORDERS: 'write::orders' as const,
  WRITE_SUBMIT_ORDERS: 'write::submit::orders' as const,
  WRITE_MODIFY_ORDERS: 'write::modify::orders' as const,
  WRITE_CANCEL_ORDERS: 'write::cancel::orders' as const,
  WRITE_PAUSE_ORDERS: 'write::pause::orders' as const,
  READ_POSITIONS: 'read::positions' as const,
  READ_CFD: 'read::cfd' as const,
  READ_CFD_FUNDING_EVENTS: 'read::cfd::funding_events' as const,
  READ_MARGIN: 'read::margin' as const,
  READ_SECMASTER: 'read::secmaster' as const,
  ADMIN_LIMITS: 'admin::limits' as const,
  ADMIN_ORDERS: 'admin::orders' as const,
  WRITE_QUOTES: 'write::quotes' as const,
  ADMIN_POSITIONS: 'admin::positions' as const,
  READ_PRICING: 'read::pricing' as const,
  READ_QUOTES: 'read::quotes' as const,
  READ_ENTITY: 'read::entity' as const,
  ADMIN_CONFIGURATIONS: 'admin::ui::configuration' as const,
};

// actions
export const WL_ACTION = {
  USE_SYMBOL_SELECTOR: 'symbolselector.use' as const,
  VIEW_BLOTTERS: 'blotters.view' as const,
  VIEW_BLOTTERS_ACTIVE_ORDERS: 'blotters.activeorders' as const,
  VIEW_BLOTTERS_ORDER_HISTORY: 'blotters.orderhistory' as const,
  VIEW_BLOTTERS_TRADES: 'blotters.trades' as const,
  VIEW_BLOTTERS_BALANCES: 'blotters.balances' as const,
  VIEW_BLOTTERS_CREDITS: 'blotters.credits' as const,
  VIEW_BLOTTERS_TRANSFERS: 'blotters.transfers' as const,
  VIEW_BLOTTERS_FUNDING_EVENTS: 'blotters.fundingevents' as const,
  BLOTTERS_TRANSFERS_WITHDRAW: 'blotters.transfers.withdraw' as const,
  BLOTTERS_TRANSFERS_EXPORT: 'blotters.transfers.export' as const,
  VIEW_BLOTTERS_POSITIONS: 'blotters.positions' as const,
  VIEW_BLOTTERS_MARGIN: 'blotters.margin' as const,
  VIEW_SETTINGS: 'settings.view' as const,
  VIEW_FEE_TIERS: 'feetiers.view' as const,
  SHOW_CANDLESTICKS_AND_MARKETDATA: 'marketdata.view' as const,
  MARKET_DATA: 'marketdata.read' as const,
  INITIATE_ORDER: 'order.init' as const,
  MODIFY_ORDER: 'order.modify' as const,
  CANCEL_ORDER: 'order.cancel' as const,
  PAUSE_ORDER: 'order.pause' as const,
  INITIATE_RFQ: 'rfq.init' as const,
  VIEW_FUNDING_SCHEDULE: 'funding.schedule.view' as const,
  SEE_CONFIG_TOOLS: 'config.tools.view' as const,
};

type WLRuleMapping = { [key: string]: string[] };

export const WL_RULES: WLRuleMapping = {
  [WL_ACTION.USE_SYMBOL_SELECTOR]: [WL_PERMISSIONS.READ_SECMASTER],
  [WL_ACTION.VIEW_BLOTTERS]: [WL_PERMISSIONS.READ_ORDERS, WL_PERMISSIONS.READ_POSITIONS],
  [WL_ACTION.VIEW_BLOTTERS_ACTIVE_ORDERS]: [WL_PERMISSIONS.READ_ORDERS],
  [WL_ACTION.VIEW_BLOTTERS_ORDER_HISTORY]: [WL_PERMISSIONS.READ_ORDERS],
  [WL_ACTION.VIEW_BLOTTERS_TRADES]: [WL_PERMISSIONS.READ_ORDERS],
  [WL_ACTION.VIEW_BLOTTERS_BALANCES]: [WL_PERMISSIONS.READ_POSITIONS],
  [WL_ACTION.VIEW_BLOTTERS_CREDITS]: [WL_PERMISSIONS.READ_POSITIONS],
  [WL_ACTION.VIEW_BLOTTERS_TRANSFERS]: [WL_PERMISSIONS.READ_POSITIONS],
  [WL_ACTION.VIEW_BLOTTERS_FUNDING_EVENTS]: [WL_PERMISSIONS.READ_CFD, WL_PERMISSIONS.READ_CFD_FUNDING_EVENTS],
  [WL_ACTION.VIEW_FUNDING_SCHEDULE]: [WL_PERMISSIONS.READ_CFD],
  [WL_ACTION.BLOTTERS_TRANSFERS_WITHDRAW]: [WL_PERMISSIONS.ADMIN_POSITIONS],
  [WL_ACTION.BLOTTERS_TRANSFERS_EXPORT]: [WL_PERMISSIONS.READ_POSITIONS],
  [WL_ACTION.VIEW_BLOTTERS_POSITIONS]: [WL_PERMISSIONS.READ_CFD],
  [WL_ACTION.VIEW_BLOTTERS_MARGIN]: [WL_PERMISSIONS.READ_MARGIN],
  [WL_ACTION.VIEW_SETTINGS]: [WL_PERMISSIONS.READ_ENTITY],
  [WL_ACTION.VIEW_FEE_TIERS]: [WL_PERMISSIONS.READ_ENTITY],
  [WL_ACTION.SHOW_CANDLESTICKS_AND_MARKETDATA]: [WL_PERMISSIONS.READ_MARKETDATA],
  [WL_ACTION.INITIATE_ORDER]: [WL_PERMISSIONS.WRITE_ORDERS, WL_PERMISSIONS.WRITE_SUBMIT_ORDERS],
  [WL_ACTION.MODIFY_ORDER]: [WL_PERMISSIONS.WRITE_ORDERS, WL_PERMISSIONS.WRITE_MODIFY_ORDERS],
  [WL_ACTION.CANCEL_ORDER]: [WL_PERMISSIONS.WRITE_ORDERS, WL_PERMISSIONS.WRITE_CANCEL_ORDERS],
  [WL_ACTION.PAUSE_ORDER]: [WL_PERMISSIONS.WRITE_ORDERS, WL_PERMISSIONS.WRITE_PAUSE_ORDERS],
  [WL_ACTION.INITIATE_RFQ]: [WL_PERMISSIONS.WRITE_QUOTES],
  [WL_ACTION.SEE_CONFIG_TOOLS]: [WL_PERMISSIONS.ADMIN_CONFIGURATIONS],
};

export const useWLRoleAuth = () => {
  const user = useWLUser();
  const isAuthorized = useCallback((action: string) => isWLUserAuthorized(user, action, WL_RULES), [user]);
  const isRole = useCallback((role: string) => isWLUserRole(user, role), [user]);

  return { isAuthorized, isRole };
};

export const isWLUserRole = (user: CustomerUser, role: string) =>
  user && user.Permissions && user.Permissions.includes(role);

export const isWLUserAuthorized = (user: CustomerUser, action: string, rules: WLRuleMapping) => {
  if (user == null || user.Permissions == null) {
    return false;
  }
  return rules[action]?.some(permission => user.Permissions?.includes(permission));
};

export const useWLUserPermissions = (): string[] => {
  const user = useWLUser();
  return user.Permissions ?? [];
};
